// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Shared Owner/Admin/Member/Viewer display layer for Admin Center's Users
 * screens (list + detail). Extracted from `UsersPage.tsx` so the detail page
 * (ADO #22697) doesn't duplicate it.
 *
 * Labels come from `shared/roles/roleLabels.ts`, shared with the profile menu.
 * They are display names, not IAM's role names — see
 * `helios/services/authService.ts` (`ROLE_ALIASES`/`toUserRole`) for the real
 * canonical mapping, confirmed in the #helios-integration Slack thread
 * (2026-09-14, Charles Stoksik + Filippo Lovotti): Admin→Admin,
 * Operations Manager→Owner, Operator→Member, Executive→Viewer.
 */

import { ROLE_LABELS } from '../../shared/roles/roleLabels';
import type { CanonicalRole } from '../../shared/roles/roleLabels';

export type TagType = 'purple' | 'green' | 'blue' | 'gray';

export const ROLE_TAG_TYPE: Record<string, TagType> = {
  admin: 'green',
  operations_manager: 'purple',
  operator: 'blue',
  executive: 'gray',
};

/** One line of UI copy per role, shown under the Organization Role picker. */
export const ORG_ROLE_DESCRIPTION: Record<string, string> = {
  admin: 'Manages users, procedures and bindings across all sites. No billing.',
  operations_manager: 'Configures operational content across assigned sites or regions.',
  operator: 'Monitors assigned sites via live telemetry. Read and recommend only.',
  executive: 'Read-only fleet oversight across the organization or a region.',
};

export function roleLabel(role: string | null): string {
  if (!role) return 'No role';
  return ROLE_LABELS[role as CanonicalRole] ?? role;
}

export function roleTagType(role: string | null): TagType {
  if (!role) return 'gray';
  return ROLE_TAG_TYPE[role] ?? 'gray';
}
